import { useMutation } from '@apollo/client';
import {
  Arrange,
  Container,
  Icon,
  Spacer,
  SvgChevronDown,
  Switch,
  Text,
  Toast,
} from '@loomhq/lens';
import React, { useMemo, useState } from 'react';
import { useLayer } from 'react-laag';
import { useDispatch } from 'react-redux';

import { closeModal, openModal } from 'actions/modals';
import { VIDEO_PUBLISH_TOGGLED } from 'constants/events';
import { PROFILE_PAGE } from 'constants/menuPages';
import { UNPUBLISH_VIDEO_CONFIRMATION_MODAL } from 'constants/modalTypes';
import { VideoVisibility as VisibilityType } from 'constants/visibility';
import UpdateVideoVisibility from 'creators/UpdateVideoVisibility.graphql';
import { track } from 'utilities/analytics';

import useClickOutside from 'hooks/useClickOutside';

import { VIDEO_PRIVACY_OWNER } from '../../../../shared/constants/video';

import styles from './styles.module.less';

type VideoVisibilityProps = {
  videoId: string;
  videoVisibility: VisibilityType;
  videoPrivacy?: string;
  textSize?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge';
  // onVisibilityUpdate is a callback to execute once the video visibility has updated
  onVisibilityUpdate?: (visibility: string) => void;
  pageType: string;
  currentUserCanEdit?: boolean;
};

export const VideoVisibility = ({
  videoVisibility,
  videoPrivacy,
  videoId,
  textSize = 'medium',
  onVisibilityUpdate,
  pageType,
  currentUserCanEdit = false,
}: VideoVisibilityProps): React.ReactElement => {
  const [isVisibilityPopoverOpen, setIsVisibilityPopoverOpen] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen: isVisibilityPopoverOpen,
    ResizeObserver,
    placement: 'bottom-start',
    auto: true,
  });

  useMemo(() => {
    const isPublished = videoVisibility?.includes(
      VisibilityType.WORKSPACE,
      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      VisibilityType.PUBLIC
    );

    setIsPublished(isPublished);
  }, [videoVisibility]);

  const onOpenPopOver = () => {
    setIsVisibilityPopoverOpen(!isVisibilityPopoverOpen);
  };

  const publishedText = (
    <Text fontWeight="medium" color="primary" size={textSize}>
      Published
    </Text>
  );
  const unpublishedText = (
    <Text color="bodyDimmed" size={textSize}>
      Not published
    </Text>
  );

  return (
    <>
      <button
        className={styles.visibilityButton}
        {...triggerProps}
        onClick={onOpenPopOver}
      >
        <Arrange gap="xsmall">
          {isPublished ? publishedText : unpublishedText}
          <Icon icon={<SvgChevronDown />} size="1.5" />
        </Arrange>
      </button>

      {isVisibilityPopoverOpen &&
        renderLayer(
          <span className={styles.visibilityCard} {...layerProps}>
            <VisibilityPopover
              videoId={videoId}
              videoPrivacy={videoPrivacy}
              isPublished={isPublished}
              setIsVisibilityPopoverOpen={setIsVisibilityPopoverOpen}
              onVisibilityUpdate={onVisibilityUpdate}
              pageType={pageType}
              setIsPublished={setIsPublished}
              currentUserCanEdit={currentUserCanEdit}
            />
          </span>
        )}
    </>
  );
};

type VisibilityPopoverProps = {
  videoId: string;
  videoPrivacy?: string;
  isPublished: boolean;
  setIsVisibilityPopoverOpen: (isOpen: boolean) => void;
  onVisibilityUpdate?: (visibility: string) => void;
  pageType: string;
  setIsPublished: (isPublished: boolean) => void;
  currentUserCanEdit: boolean;
};

const VisibilityPopover = ({
  videoId,
  videoPrivacy,
  isPublished,
  setIsVisibilityPopoverOpen,
  onVisibilityUpdate,
  pageType,
  setIsPublished,
  currentUserCanEdit,
}: VisibilityPopoverProps): React.ReactElement => {
  const [isUpdateVisibilityError, setIsUpdateVisibilityError] = useState(false);
  const ref = useClickOutside(() => setIsVisibilityPopoverOpen(false));
  const dispatch = useDispatch();

  const isPrivate = videoPrivacy === VIDEO_PRIVACY_OWNER;

  const [updateVideoVisibility, { loading }] = useMutation(
    UpdateVideoVisibility,
    {
      onCompleted: data => {
        onVisibilityUpdate &&
          onVisibilityUpdate(data.updateVideoVisibility.video.visibility);
        setIsUpdateVisibilityError(false);
      },
      onError: () => {
        setIsUpdateVisibilityError(true);
        setIsPublished(!isPublished);
      },
      refetchQueries: ['GetWorkspaceUserFromProfileUrl'],
    }
  );

  const updateVisibility = () => {
    const oldVisibility = isPublished
      ? VisibilityType.WORKSPACE
      : VisibilityType.OWNER;

    const newVisibility = isPublished
      ? VisibilityType.OWNER
      : VisibilityType.WORKSPACE;

    updateVideoVisibility({
      variables: {
        videoId,
        visibility: newVisibility,
      },
    });

    setIsPublished(!isPublished);

    track(VIDEO_PUBLISH_TOGGLED, {
      video_id: videoId,
      context_page_path: window.location.pathname,
      toggled_from: oldVisibility,
      toggled_to: newVisibility,
    });

    dispatch(closeModal());
  };

  const onVisibilitySwitchChange = () => {
    // Don't allow updating visibility if visibility
    // update is not complete (don't want someone spamming)
    if (loading) {
      return;
    }

    // in contexts where we only show published videos, we want
    // to prompt the user to let them know the consequences
    // of unpublishing and where to publish
    if (isPublished && pageType === PROFILE_PAGE) {
      dispatch(
        openModal(UNPUBLISH_VIDEO_CONFIRMATION_MODAL, {
          onUnpublish: updateVisibility,
        })
      );
    }

    setIsUpdateVisibilityError(false);

    if (pageType !== PROFILE_PAGE) {
      updateVisibility();
    }
  };

  const message = !currentUserCanEdit
    ? isPublished
      ? 'Only an editor of this video can unpublish it.'
      : 'Only an editor of this video can publish it.'
    : isPrivate
    ? 'This video is not visible to the Workspace. Change its privacy setting in order to enable publishing.'
    : 'A published video can appear in search results, profiles, and tag pages.';

  return (
    <div
      onClick={e => {
        e.stopPropagation();
      }}
      ref={ref}
    >
      <Container
        padding="medium"
        backgroundColor="background"
        borderSide="all"
        radius="medium"
        shadow="medium"
        width="17.5rem"
      >
        <Arrange justifyContent="space-between">
          <Text fontWeight="bold">Publish this video</Text>
          <Switch
            onChange={onVisibilitySwitchChange}
            isActive={isPublished}
            isDisabled={isPrivate || !currentUserCanEdit}
          />
        </Arrange>
        <Spacer top="medium" />
        <Text variant="body" htmlTag="p" color="bodyDimmed">
          {message}
        </Text>
      </Container>

      <Toast
        isOpen={Boolean(isUpdateVisibilityError)}
        onCloseClick={() => setIsUpdateVisibilityError(false)}
        zIndex={5}
      >
        Failed to update video visibility.
      </Toast>
    </div>
  );
};
